import type { ReactNode } from "react";

/**
 * The handful of pieces every portal screen needs. Hand-written rather than imported: a component
 * library and an icon package are both explicitly out (docs/01-PRODUCT-SPEC.md §13 — this app has
 * a 150 KB gzipped budget and ships to phones on Nigerian mobile networks).
 */

/** Placeholder that holds the shape of what is loading, so the screen does not jump when it lands. */
export const Skeleton = ({ className }: { className: string }): JSX.Element => (
  <span className={`sga-skeleton block ${className}`} aria-hidden="true" />
);

/** Loading state for a list of cards — same height as the real thing. */
export const CardSkeleton = ({ rows = 3 }: { rows?: number }): JSX.Element => (
  <div className="space-y-3" aria-busy="true" aria-live="polite" aria-label="Loading">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="sga-card p-4">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="mt-3 h-3 w-1/3" />
        <Skeleton className="mt-2 h-3 w-1/4" />
      </div>
    ))}
  </div>
);

export const Notice = ({
  tone = "info",
  children,
}: {
  tone?: "info" | "error";
  children: ReactNode;
}): JSX.Element => (
  <p
    className={`sga-notice ${tone === "error" ? "sga-notice--error" : ""}`}
    role={tone === "error" ? "alert" : undefined}
  >
    {children}
  </p>
);

/** Nothing to show yet, said in a way that tells the applicant what would put something here. */
export const EmptyState = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element => (
  <div className="sga-card px-6 py-10 text-center">
    <p className="font-heading text-base font-semibold">{title}</p>
    <p className="sga-muted mx-auto mt-2 max-w-md text-sm">{children}</p>
  </div>
);

/**
 * A stored answer, as text. Objects fall through to JSON — a deliberate last resort that shows the
 * applicant *something* rather than an empty row for a field type this screen has not learned yet.
 */
export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.map((entry) => String(entry)).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Dates are stored UTC and shown in Africa/Lagos, at the presentation layer and nowhere else
 * (CLAUDE.md). `Intl` does the conversion — there is no date library in this app and no arithmetic
 * on strings.
 */
export const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    timeZone: "Africa/Lagos",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/** Icons, inline and hand-drawn. Decorative in every use here, so hidden from assistive tech. */
const icon = (path: ReactNode) =>
  function Icon({ className = "h-4 w-4" }: { className?: string }): JSX.Element {
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {path}
      </svg>
    );
  };

export const ArrowRight = icon(<path d="M5 12h14M13 6l6 6-6 6" />);
export const ArrowLeft = icon(<path d="M19 12H5M11 18l-6-6 6-6" />);
export const Download = icon(
  <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />,
);
export const Eye = icon(
  <>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const Check = icon(<path d="M20 6 9 17l-5-5" />);
